/** Which layers are drawn, in what order, how strongly, and how the trees are coloured. */
import { Fragment, useId, useState, type ReactNode } from 'react';
import { ActionIcon, Button, Card, Checkbox, Divider, Group, MultiSelect, RangeSlider, SegmentedControl, Select, Slider, Stack, Switch, Text, UnstyledButton } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { MAP, speciesColor, STATUS_COLORS } from '@/theme/colors';
import { brand } from '@/theme/theme';
import { useFormat } from '@/components/useFormat';
import { chmLegend, DEFAULT_RANGES, HEALTH_COLOR_BY, isVisible, type Basemap, type FilterRange, opacityOf, rampColors, type ColorBy, CROWN_STYLES, type CrownStyle, hasPositioning, PHOTO_RANGES, photoColorByOf, type PhotoColorBy, type Stratum, type LayerOptions, type LayerRef, type PhotoObjectRef } from './layers';
import { groupOf, isCrownLayer, isHealthLayer, isTreeLayer, LAYER_GROUPS, layerKey, positionSourceEntries, sampleOf, type LayerGroup, type LegendEntry } from './layerCatalog';
import { LayerSample } from './LayerSample';
import { MapLegend, Scale } from './MapLegend';
import { useGenusName } from './genusName';

/** A labelled choice the page offers (a value and what it reads). */
export interface PanelOption {
  value: string;
  label: string;
}

/**
 * What a page lays under its layers, chosen in the panel: the page keeps the choice and swaps the
 * raster it passes in `refs`; the panel only shows the switch, its sub-choice and its ramp.
 */
export interface BackdropChoice {
  /** The section's title, in the page's words. */
  title: string;
  choices: PanelOption[];
  value: string;
  onChange: (value: string) => void;
  /** A second choice the current one needs (the band of a one-band view). */
  detail?: { label: string; choices: PanelOption[]; value: string; onChange: (value: string) => void };
  /** The colour scale of the current choice, with its values (an index's ramp). */
  scale?: LegendEntry;
}

export interface LayerPanelProps {
  /** The map is narrow: before the reader has chosen, the panel starts folded away. */
  narrow?: boolean;
  refs: LayerRef[];
  options: LayerOptions;
  onOptionsChange: (options: LayerOptions) => void;
  photos?: PhotoObjectRef[];
  /** The tiler's layout warnings per raster object (`no_overviews`, …). */
  warnings?: Record<string, string[]>;
  /** Rasters whose metadata the tiler refused; they stay listed but may not draw. */
  metaErrors?: Record<string, unknown>;
  /** Band-1 value range per raster object, from the tiler's metadata; a visible CHM shows it as a legend. */
  valueRanges?: Record<string, [number, number]>;
  /** The trees report adds maturity and height layers to each crown; offer their controls. */
  treeExtras?: boolean;
  /** The genera the tree layers hold, which the genus filter offers and the legend lists. */
  genera?: string[];
  /** The unit levels the units drawn hold, for the legend. */
  unitLevels?: string[];
  /** The page's switch of what lies under its layers; shown only with more than one choice. */
  backdrop?: BackdropChoice;
}

const COLOR_BY: ColorBy[] = ['species', 'height', 'dbh', 'volume'];
const BASEMAP_CHOICES: Basemap[] = ['none', 'osm', 'ortho'];

/** The drawable objects in the order they are currently stacked. */
function ordered(refs: LayerRef[], order: string[] | undefined): LayerRef[] {
  const drawn = refs.filter((r) => r.via !== 'none');
  if (!order?.length) return drawn;
  const rank = new Map(order.map((id, i) => [id, i]));
  return [...drawn]
    .map((r, i) => ({ r, key: rank.get(r.object) ?? order.length + i }))
    .sort((l, r) => l.key - r.key)
    .map(({ r }) => r);
}

interface CommittedRangeProps {
  bounds: [number, number];
  step?: number;
  value: [number, number];
  /** The released range, `undefined` when both thumbs sit at the ends. */
  onCommit: (value: FilterRange | undefined) => void;
}

/** The thumbs of a filter range: an open side sits at the slider's end. */
function thumbsOf(filter: FilterRange | undefined, bounds: [number, number]): [number, number] {
  return [filter?.[0] ?? bounds[0], filter?.[1] ?? bounds[1]];
}

/**
 * A range filter. A controlled slider needs a value that follows the drag, but only the
 * released range is worth a style rebuild, so the thumbs live here and the panel hears the end.
 * A thumb at an end opens that side: the slider's ends are the colour ramp's, not the data's.
 */
function CommittedRange({ bounds, step = 1, value, onCommit }: CommittedRangeProps) {
  const [range, setRange] = useState<[number, number]>(value);
  const commit = ([lo, hi]: [number, number]) => {
    const open: FilterRange = [lo <= bounds[0] ? null : lo, hi >= bounds[1] ? null : hi];
    onCommit(open[0] === null && open[1] === null ? undefined : open);
  };
  return <RangeSlider size="xs" min={bounds[0]} max={bounds[1]} step={step} value={range} onChange={setRange} onChangeEnd={commit} />;
}

/** The viridis bar the tiler colours a CHM with, labelled with the heights at its ends. */
const PHOTO_COLOR_BY: PhotoColorBy[] = ['none', 'position_source', 'agl_m', 'forward_overlap_pct', 'side_overlap_pct', 'tilt_deg', 'geo_error_h_m'];

/** The photo points' colours by the source of their camera position, one swatch per source. */
function PositionSourceLegend({ id }: { id: string }) {
  const { t } = useTranslation('viewer');
  return (
    <Stack gap={2} data-testid={id} aria-label={t('photoColorBy.position_source')}>
      {positionSourceEntries().map((e) => (
        <Group key={e.id} gap={6} wrap="nowrap" data-testid={`legend-${e.id}`}>
          <LayerSample sample={e.sample} />
          <Text fz={11} c={brand.textMuted}>
            {t(e.label)}
          </Text>
        </Group>
      ))}
    </Stack>
  );
}

function RampLegend({ id, range, colors = rampColors(), unit, label }: { id: string; range: [number, number]; colors?: string[]; unit: string; label: string }) {
  const { number } = useFormat();
  const [low, high] = range;
  return (
    <Stack gap={2} data-testid={id} aria-label={label}>
      <div style={{ height: 8, borderRadius: 4, background: `linear-gradient(to right, ${colors.join(', ')})` }} />
      <Group justify="space-between">
        <Text fz={11} c={brand.textMuted}>
          {number(low)} {unit}
        </Text>
        <Text fz={11} c={brand.textMuted}>
          {number(high)} {unit}
        </Text>
      </Group>
    </Stack>
  );
}

function ChmLegend({ id, range }: { id: string; range: [number, number] }) {
  const { t } = useTranslation('viewer');
  const { low, high, colors } = chmLegend(range);
  return <RampLegend id={id} unit={t('units.m')} range={[low, high]} colors={colors} label={t('layers.chmLegend')} />;
}

/** Where the browser keeps whether the panel is folded away, for every map alike. */
export const LAYER_PANEL_COLLAPSED_KEY = 'forestum.layerPanel.collapsed';

function storedCollapsed(): boolean | null {
  try {
    const v = window.localStorage.getItem(LAYER_PANEL_COLLAPSED_KEY);
    return v === null ? null : v === '1';
  } catch {
    return null;
  }
}

/** Where the browser keeps which groups of the panel are folded, for every map alike. */
export const LAYER_GROUPS_FOLDED_KEY = 'forestum.layerPanel.folded';

function storedFolded(): string[] {
  try {
    const v: unknown = JSON.parse(window.localStorage.getItem(LAYER_GROUPS_FOLDED_KEY) ?? '[]');
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

/** A key with a ring and three lines: the legend. */
function LegendIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <circle cx="5" cy="6" r="2" />
      <circle cx="5" cy="12" r="2" />
      <circle cx="5" cy="18" r="2" />
      <path d="M10 6h11M10 12h11M10 18h11" />
    </svg>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ transform: open ? 'rotate(90deg)' : undefined }}>
      <path d="m9 6 6 6-6 6" />
    </svg>
  );
}

/** Two sliders: a layer's own adjustments, folded under its row. */
function TuneIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <path d="M4 7h10M18 7h2M4 17h2M10 17h10" />
      <circle cx="16" cy="7" r="2" />
      <circle cx="8" cy="17" r="2" />
    </svg>
  );
}

/** A titled, foldable part of the panel. */
function PanelGroup({ id, title, count, folded, onFold, children }: { id: string; title: string; count?: number; folded: boolean; onFold: (folded: boolean) => void; children: ReactNode }) {
  const bodyId = useId();
  return (
    <Stack gap={6} data-testid={`layer-group-${id}`}>
      <UnstyledButton onClick={() => onFold(!folded)} aria-expanded={!folded} aria-controls={bodyId} data-testid={`layer-group-toggle-${id}`}>
        <Group gap={6} wrap="nowrap" c={brand.textMuted}>
          <Chevron open={!folded} />
          <Text fz={11} fw={700} tt="uppercase" c={brand.textMuted} style={{ letterSpacing: '0.06em' }}>
            {title}
          </Text>
          {count !== undefined && (
            <Text fz={11} c={brand.textMuted}>
              {count}
            </Text>
          )}
        </Group>
      </UnstyledButton>
      {!folded && (
        <Stack id={bodyId} gap={8} pl={4}>
          {children}
        </Stack>
      )}
    </Stack>
  );
}

interface LayerRowProps {
  name: string;
  sample: ReturnType<typeof sampleOf>;
  visible: boolean;
  opacity: number;
  onVisible: (visible: boolean) => void;
  onOpacity: (opacity: number) => void;
  onUp?: () => void;
  onDown?: () => void;
  children?: ReactNode;
}

/** A layer: its switch, its sample and name; its opacity and place in the stack folded under it. */
function LayerRow({ name, sample, visible, opacity, onVisible, onOpacity, onUp, onDown, children }: LayerRowProps) {
  const { t } = useTranslation('viewer');
  const [tuning, setTuning] = useState(false);
  const tuneId = useId();
  return (
    <Stack gap={4}>
      <Group justify="space-between" wrap="nowrap" gap={4}>
        <Switch
          size="xs"
          checked={visible}
          onChange={(e) => onVisible(e.currentTarget.checked)}
          aria-label={t('layers.toggle', { name })}
          label={
            <Group gap={6} wrap="nowrap" component="span">
              {sample && <LayerSample sample={sample} />}
              <Text span fz={13} lh={1.25}>
                {name}
              </Text>
            </Group>
          }
          styles={{ body: { alignItems: 'center' } }}
        />
        <ActionIcon size="sm" variant={tuning ? 'light' : 'subtle'} color="gray" aria-label={t('layers.adjust', { name })} aria-expanded={tuning} aria-controls={tuneId} onClick={() => setTuning(!tuning)}>
          <TuneIcon />
        </ActionIcon>
      </Group>
      {tuning && (
        <Group id={tuneId} gap={4} wrap="nowrap" pl={36}>
          <Slider size="xs" min={0} max={1} step={0.05} label={null} aria-label={t('layers.opacity', { name })} value={opacity} onChange={onOpacity} style={{ flex: 1 }} />
          {(onUp || onDown) && (
            <>
              <ActionIcon size="sm" variant="subtle" aria-label={t('layers.up')} disabled={!onUp} onClick={onUp}>
                ↑
              </ActionIcon>
              <ActionIcon size="sm" variant="subtle" aria-label={t('layers.down')} disabled={!onDown} onClick={onDown}>
                ↓
              </ActionIcon>
            </>
          )}
        </Group>
      )}
      {children}
    </Stack>
  );
}

/** The one genus choice every tree layer follows: a swatch of the genus colour and its name. */
function GenusFilter({ genera, value, onChange }: { genera: string[]; value: string[]; onChange: (value: string[]) => void }) {
  const { t, i18n } = useTranslation('viewer');
  const name = useGenusName();
  // A genus chosen before the data says what it holds stays offered, so it can be dropped.
  const offered = [...new Set([...genera, ...value])];
  const data = offered.map((g) => ({ value: g, label: name(g) })).sort((a, b) => a.label.localeCompare(b.label, i18n.language));
  return (
    <MultiSelect
      size="xs"
      label={t('filters.species')}
      description={t('filters.speciesHint')}
      placeholder={value.length ? undefined : t('filters.speciesPlaceholder')}
      value={value}
      data={data}
      searchable
      clearable
      hidePickedOptions={false}
      nothingFoundMessage={t('filters.speciesNone')}
      onChange={onChange}
      renderOption={({ option, checked }) => (
        <Group gap={8} wrap="nowrap" data-testid={`genus-option-${option.value}`}>
          <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 5, flex: 'none', background: speciesColor(option.value), outline: checked ? `2px solid ${brand.text}` : undefined }} />
          <Text fz="sm">{option.label}</Text>
          {option.label !== option.value && (
            <Text fz="xs" c={brand.textMuted} fs="italic">
              {option.value}
            </Text>
          )}
        </Group>
      )}
      data-testid="genus-filter"
    />
  );
}

/** Three stacked sheets: the panel folded away. */
function LayersIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3 2 8l10 5 10-5-10-5Z" />
      <path d="m2 13 10 5 10-5" />
      <path d="m2 18 10 5 10-5" />
    </svg>
  );
}

export function LayerPanel({ refs, options, onOptionsChange, photos, warnings, metaErrors, valueRanges, treeExtras, genera, unitLevels, backdrop, narrow = false }: LayerPanelProps) {
  const { t } = useTranslation('viewer');
  const panelId = useId();
  // The reader's last choice in this browser; before any, folded away on a narrow map only.
  const [collapsed, setCollapsed] = useState(() => storedCollapsed() ?? narrow);
  const [folded, setFolded] = useState<string[]>(storedFolded);
  const [legend, setLegend] = useState(false);
  const photoBy = photoColorByOf(options, photos ?? []);
  const fold = (next: boolean) => {
    setCollapsed(next);
    try {
      window.localStorage.setItem(LAYER_PANEL_COLLAPSED_KEY, next ? '1' : '0');
    } catch {
      // A browser that keeps nothing still folds the panel for now.
    }
  };
  const foldGroup = (group: string, next: boolean) => {
    const list = next ? [...folded.filter((g) => g !== group), group] : folded.filter((g) => g !== group);
    setFolded(list);
    try {
      window.localStorage.setItem(LAYER_GROUPS_FOLDED_KEY, JSON.stringify(list));
    } catch {
      // A browser that keeps nothing still folds the group for now.
    }
  };
  const rows = ordered(refs, options.layers);
  const hasTrees = rows.some(isTreeLayer);
  // An MLS analysis's stems and crowns take the same colouring, crown style and filters as photo crowns.
  const hasCrowns = rows.some(isCrownLayer);
  const hasHealth = rows.some(isHealthLayer);
  const set = (patch: Partial<LayerOptions>) => onOptionsChange({ ...options, ...patch });
  const setSettings = (object: string, patch: { visible?: boolean; opacity?: number }) =>
    set({ settings: { ...options.settings, [object]: { ...options.settings?.[object], ...patch } } });

  // A layer moves past its neighbour in its own group; the stack across groups keeps its order.
  const move = (object: string, neighbour: string) => {
    const ids = rows.map((r) => r.object);
    const a = ids.indexOf(object);
    const b = ids.indexOf(neighbour);
    [ids[a], ids[b]] = [ids[b]!, ids[a]!];
    set({ layers: ids });
  };

  const heightBounds = options.ranges?.height ?? DEFAULT_RANGES.height;
  const [low, high] = thumbsOf(options.filters?.height, heightBounds);
  const dbhBounds = options.ranges?.dbh ?? DEFAULT_RANGES.dbh;
  const [dbhLow, dbhHigh] = thumbsOf(options.filters?.dbh, dbhBounds);
  const species = options.filters?.species ?? [];
  const offeredGenera = [...new Set([...(genera ?? []), ...(options.species ?? [])])].sort();

  const legendModal = (
    <MapLegend opened={legend} onClose={() => setLegend(false)} refs={refs} options={options} photos={photos} genera={offeredGenera} unitLevels={unitLevels} valueRanges={valueRanges} />
  );
  const legendButton = (
    <ActionIcon size="sm" variant="subtle" color="gray" aria-label={t('legend.open')} onClick={() => setLegend(true)} data-testid="legend-open">
      <LegendIcon />
    </ActionIcon>
  );

  if (collapsed) {
    return (
      <Group gap={6} wrap="nowrap">
        <Button variant="default" size="xs" radius="lg" className="report-actions" leftSection={<LegendIcon />} onClick={() => setLegend(true)} data-testid="legend-open">
          {t('legend.button')}
        </Button>
        <Button
          variant="default"
          size="xs"
          radius="lg"
          className="report-actions"
          leftSection={<LayersIcon />}
          aria-expanded={false}
          aria-controls={panelId}
          onClick={() => fold(false)}
          data-testid="layer-panel-toggle"
        >
          {t('layers.title')}
          {species.length > 0 && ` · ${t('filters.speciesActive', { count: species.length })}`}
        </Button>
        {legendModal}
      </Group>
    );
  }

  const nameOf = (ref: LayerRef) => {
    const kind = t(`kinds.${ref.object_kind}`);
    const table = ref.table ? t(`tables.${ref.table}`, { defaultValue: kind }) : kind;
    return t(`layers.names.${layerKey(ref)}`, { defaultValue: table });
  };

  const rowOf = (ref: LayerRef, i: number, group: LayerRef[]) => {
    const name = nameOf(ref);
    const valueRange = valueRanges?.[ref.object];
    const visible = isVisible(options, ref.object, ref.object_kind);
    const above = group[i - 1];
    const below = group[i + 1];
    return (
      <LayerRow
        key={ref.object}
        name={name}
        sample={sampleOf(ref, options.colorBy)}
        visible={visible}
        opacity={opacityOf(options, ref.object, ref.object_kind)}
        onVisible={(v) => setSettings(ref.object, { visible: v })}
        onOpacity={(v) => setSettings(ref.object, { opacity: v })}
        onUp={above ? () => move(ref.object, above.object) : undefined}
        onDown={below ? () => move(ref.object, below.object) : undefined}
      >
        {ref.via === 'tiler' && metaErrors?.[ref.object] !== undefined ? (
          <Text fz={11} c={STATUS_COLORS.failed.color} data-testid={`layer-warning-${ref.object}`}>
            {t('layers.unavailable')}
          </Text>
        ) : ref.via === 'tiler' && warnings?.[ref.object]?.length ? (
          <Text fz={11} c={brand.textMuted} data-testid={`layer-warning-${ref.object}`}>
            {warnings[ref.object]!.map((w) => t(`layers.warnings.${w}`, { defaultValue: w })).join(', ')}
          </Text>
        ) : null}
        {ref.object_kind === 'chm' && valueRange && visible && <ChmLegend id={`chm-legend-${ref.object}`} range={valueRange} />}
      </LayerRow>
    );
  };

  // Filled or outlined: photo and MLS crowns and the crown health screen's crowns alike.
  const crownStyleControl = (
    <Stack gap={2}>
      <Text fz={12} c={brand.textMuted}>
        {t('crownStyle.label')}
      </Text>
      <SegmentedControl
        size="xs"
        fullWidth
        value={options.crownStyle ?? 'fill'}
        onChange={(v) => set({ crownStyle: v as CrownStyle })}
        data={CROWN_STYLES.map((v) => ({ value: v, label: t(`crownStyle.${v}`) }))}
        data-testid="crown-style"
      />
    </Stack>
  );

  const crownControls = (
    <>
      {crownStyleControl}
      <Stack gap={2}>
        <Text fz={12} c={brand.textMuted}>
          {t('filters.height')}
        </Text>
        <CommittedRange
          // A filter set from outside reseeds the thumbs by remounting.
          key={`${low}-${high}`}
          bounds={heightBounds}
          value={[low, high]}
          onCommit={(v) => set({ filters: { ...options.filters, height: v } })}
        />
      </Stack>
      {treeExtras && (
        <>
          <Stack gap={2}>
            <Text fz={12} c={brand.textMuted}>
              {t('filters.dbh')}
            </Text>
            <CommittedRange key={`dbh-${dbhLow}-${dbhHigh}`} bounds={dbhBounds} value={[dbhLow, dbhHigh]} onCommit={(v) => set({ filters: { ...options.filters, dbh: v } })} />
          </Stack>
          <Checkbox.Group label={t('filters.strata')} size="xs" value={options.filters?.strata ?? []} onChange={(v) => set({ filters: { ...options.filters, strata: v as Stratum[] } })}>
            <Group gap="xs" mt={4}>
              {(['upper', 'middle', 'lower'] as Stratum[]).map((s) => (
                <Checkbox key={s} value={s} label={t(`filters.stratum.${s}`)} />
              ))}
            </Group>
          </Checkbox.Group>
          <Switch
            size="xs"
            label={t('filters.matureOnly')}
            checked={!!options.filters?.matureOnly}
            onChange={(e) => set({ filters: { ...options.filters, matureOnly: e.currentTarget.checked } })}
          />
          <Switch size="xs" label={t('filters.highlightMature')} checked={!!options.highlightMature} onChange={(e) => set({ highlightMature: e.currentTarget.checked })} />
        </>
      )}
    </>
  );

  // The colouring applies to every tree layer, field trunks included; style and filters are the crowns'.
  const treeControls = hasTrees && (
    <>
      <Divider color={brand.border} />
      <Select
        size="xs"
        label={t('colorBy.label')}
        description={t('colorBy.hint')}
        value={options.colorBy ?? 'species'}
        data={(hasHealth ? HEALTH_COLOR_BY : [...COLOR_BY, ...(treeExtras ? (['maturity'] as ColorBy[]) : [])]).map((v) => ({ value: v, label: t(`colorBy.${v}`) }))}
        allowDeselect={false}
        onChange={(v) => v && set({ colorBy: v as ColorBy })}
      />
      {hasCrowns && crownControls}
      {hasHealth && !hasCrowns && crownStyleControl}
      {hasHealth && (
        <Switch
          size="xs"
          label={t('filters.flaggedOnly')}
          checked={!!options.filters?.flaggedOnly}
          onChange={(e) => set({ filters: { ...options.filters, flaggedOnly: e.currentTarget.checked } })}
          data-testid="flagged-only"
        />
      )}
    </>
  );

  const groupRows = (group: LayerGroup) => rows.filter((r) => groupOf(r) === group);

  // Stacked rather than side by side: six choices do not fit the panel's width in a row, and each stays one click.
  const backdropGroup = backdrop && backdrop.choices.length > 1 && (
    <PanelGroup key="backdrop" id="backdrop" title={backdrop.title} folded={folded.includes('backdrop')} onFold={(f) => foldGroup('backdrop', f)}>
      <SegmentedControl size="xs" fullWidth orientation="vertical" aria-label={backdrop.title} value={backdrop.value} onChange={backdrop.onChange} data={backdrop.choices} data-testid="backdrop-choice" />
      {backdrop.detail && (
        <Select
          size="xs"
          label={backdrop.detail.label}
          value={backdrop.detail.value}
          data={backdrop.detail.choices}
          allowDeselect={false}
          onChange={(v) => v && backdrop.detail!.onChange(v)}
          data-testid="backdrop-detail"
        />
      )}
      {backdrop.scale && <Scale entry={backdrop.scale} />}
    </PanelGroup>
  );

  return (
    <Card id={panelId} padding="sm" radius="lg" shadow="md" w={280} data-testid="layer-panel" style={{ maxHeight: '70%', overflowY: 'auto' }}>
      <Stack gap="sm">
        <Group justify="space-between" wrap="nowrap">
          <Group gap={6} wrap="nowrap" c={brand.textMuted}>
            <LayersIcon />
            <Text fz={11} fw={700} tt="uppercase" c={brand.textMuted} style={{ letterSpacing: '0.06em' }}>
              {t('layers.title')}
            </Text>
          </Group>
          <Group gap={2} wrap="nowrap">
            {legendButton}
            <ActionIcon size="sm" variant="subtle" color="gray" aria-label={t('layers.collapse')} aria-expanded aria-controls={panelId} onClick={() => fold(true)} data-testid="layer-panel-collapse">
              <Chevron open={false} />
            </ActionIcon>
          </Group>
        </Group>

        {!rows.length && !photos?.length && (
          <Text fz={12} c={brand.textMuted}>
            {t('layers.empty')}
          </Text>
        )}

        {hasTrees && <GenusFilter genera={offeredGenera} value={species} onChange={(v) => set({ filters: { ...options.filters, species: v } })} />}

        {LAYER_GROUPS.map((group) => {
          const list = groupRows(group);
          // The backdrop's switch sits right above the rasters it picks among.
          const before = group === 'rasters' ? backdropGroup : null;
          if (!list.length) return before;
          return (
            <Fragment key={group}>
              {before}
              <PanelGroup id={group} title={t(`layers.groups.${group}`)} count={list.length} folded={folded.includes(group)} onFold={(f) => foldGroup(group, f)}>
                {list.map((ref, i) => rowOf(ref, i, list))}
                {group === 'trees' && treeControls}
              </PanelGroup>
            </Fragment>
          );
        })}

        {!!photos?.length && (
          <PanelGroup id="photos" title={t('layers.groups.photos')} count={photos.length} folded={folded.includes('photos')} onFold={(f) => foldGroup('photos', f)}>
            {photos.map((ref) => {
              const name = t('kinds.drone_photos_raw');
              return (
                <LayerRow
                  key={ref.object}
                  name={name}
                  sample={{ kind: 'point', look: { fill: MAP.photoPoint, stroke: MAP.photoOutline, strokeWidth: 1 } }}
                  visible={options.settings?.[ref.object]?.visible !== false}
                  opacity={options.settings?.[ref.object]?.opacity ?? 1}
                  onVisible={(v) => setSettings(ref.object, { visible: v })}
                  onOpacity={(v) => setSettings(ref.object, { opacity: v })}
                />
              );
            })}
            <Select
              size="xs"
              label={t('photoColorBy.label')}
              value={photoBy}
              data={PHOTO_COLOR_BY.filter((v) => v !== 'position_source' || hasPositioning(photos)).map((v) => ({ value: v, label: t(`photoColorBy.${v}`) }))}
              allowDeselect={false}
              onChange={(v) => v && set({ photoColorBy: v as PhotoColorBy })}
            />
            {photoBy === 'position_source' && <PositionSourceLegend id={`photo-legend-${photos[0]!.object}`} />}
            {photoBy !== 'none' && photoBy !== 'position_source' && (
              <RampLegend
                id={`photo-legend-${photos[0]!.object}`}
                range={[...PHOTO_RANGES[photoBy].range]}
                unit={t(PHOTO_RANGES[photoBy].unitKey)}
                label={t(`photoColorBy.${photoBy}`)}
              />
            )}
          </PanelGroup>
        )}

        <PanelGroup id="basemap" title={t('layers.groups.basemap')} folded={folded.includes('basemap')} onFold={(f) => foldGroup('basemap', f)}>
          <SegmentedControl
            size="xs"
            fullWidth
            aria-label={t('layers.basemap')}
            value={options.basemap ?? 'osm'}
            onChange={(v) => set({ basemap: v as Basemap })}
            data={BASEMAP_CHOICES.map((v) => ({ value: v, label: t(`layers.basemaps.${v}`) }))}
          />
        </PanelGroup>
      </Stack>
      {legendModal}
    </Card>
  );
}
